import React from 'react';
import { Helmet } from 'react-helmet';
import HeroSection from './components/HeroSection';
import JobCard from './components/JobCard';
import Footer from '../ai-dating-service-landing-page/components/Footer';
import { JobListing } from './types';
import Icon from '../../components/AppIcon';

const CareersPage: React.FC = () => {
  const jobListings: JobListing[] = [
    {
      id: 'manager-interviewer',
      title: 'Менеджер-интервьюер',
      department: 'Web3 AI-Dating',
      location: 'Удаленно',
      salaryRange: '$50 за интервью',
      description: 'Инновационный Web3 проект AIDATING.LOVE ищет специалистов для проведения глубинных интервью. Мы создаем цифровых аватаров для знакомств, и твоя задача — помочь алгоритму узнать истинную личность человека.',
      responsibilities: [
        'Принимать входящие заявки от теплых лидов',
        'Назначать встречи в Zoom/Google Meet',
        'Проводить видео-интервью с клиентами',
        'Создавать доверительную атмосферу для искренних ответов',
        'Разговорить клиента и получить максимально глубокие инсайты'
      ],
      requirements: [
        'Высокая эмпатия - умение слушать так, что люди раскрываются',
        'Стабильный интернет и тихое место для звонков',
        'Интерес к Web3 и AI технологиям будет плюсом',
        'Коммуникабельность и умение вести живой диалог',
        'Английский язык на уровне разговорного (желательно)'
      ],
      benefits: [
        'Оплата: $50 за одно интервью',
        'Средняя ставка: $25/час (цикл сделки ~2 часа)',
        '100% удаленная работа',
        'Международный контракт (Web3)',
        'Идеально для держателей DTV виз и цифровых кочевников',
        'Гибкий график работы',
        'Никакой бюрократии - интервью записывается автоматически'
      ],
      applicationMessage: 'Здравствуйте! Меня заинтересовала вакансия Менеджер-интервьюер (Web3 AI-Dating)'
    },
    {
      id: 'lead-generator',
      title: 'Лидогенератор',
      department: 'Продажи и маркетинг',
      location: 'Удаленно',
      salaryRange: '$30-50 за квалифицированный лид',
      description: 'Мы ищем талантливого лидогенератора для привлечения потенциальных клиентов в наш инновационный AI-Dating сервис. Твоя задача — находить людей, которые ищут настоящую любовь через технологии будущего.',
      responsibilities: [
        'Поиск и привлечение потенциальных клиентов через различные каналы',
        'Работа с таргетированной рекламой и SMM',
        'Квалификация лидов и передача их команде интервьюеров',
        'Анализ эффективности различных источников трафика',
        'Создание и оптимизация воронок продаж',
        'Работа с CRM системой для отслеживания лидов'
      ],
      requirements: [
        'Опыт работы в лидогенерации от 1 года',
        'Знание современных инструментов digital-маркетинга',
        'Умение работать с Facebook Ads, Google Ads, Instagram',
        'Навыки копирайтинга для создания продающих текстов',
        'Аналитическое мышление и внимание к деталям',
        'Знание английского языка на уровне B2+'
      ],
      benefits: [
        'Оплата: $30-50 за каждый квалифицированный лид',
        'Бонусы за перевыполнение плана',
        '100% удаленная работа из любой точки мира',
        'Гибкий график работы',
        'Обучение от опытных маркетологов',
        'Возможность роста до Team Lead',
        'Международный контракт (Web3)'
      ],
      applicationMessage: 'Здравствуйте! Меня заинтересовала вакансия Лидогенератор'
    },
    {
      id: 'supervisor',
      title: 'Супервизор',
      department: 'Управление командой',
      location: 'Удаленно',
      salaryRange: '$3000-5000/месяц',
      description: 'Ищем опытного супервизора для управления командой менеджеров-интервьюеров. Ты будешь отвечать за качество проведения интервью, обучение команды и достижение KPI проекта.',
      responsibilities: [
        'Управление командой из 5-10 менеджеров-интервьюеров',
        'Контроль качества проведенных интервью',
        'Обучение и наставничество новых сотрудников',
        'Анализ и оптимизация процесса интервьюирования',
        'Проведение еженедельных встреч с командой',
        'Мониторинг KPI и достижение целевых показателей',
        'Работа с обратной связью от клиентов'
      ],
      requirements: [
        'Опыт руководства командой от 2 лет',
        'Отличные коммуникативные навыки',
        'Опыт работы в сфере продаж или клиентского сервиса',
        'Умение мотивировать команду и решать конфликты',
        'Навыки работы с аналитикой и отчетностью',
        'Знание английского языка на уровне C1',
        'Опыт работы в стартапах будет плюсом'
      ],
      benefits: [
        'Фиксированная зарплата: $3000-5000/месяц',
        'Бонусы за достижение командных KPI',
        '100% удаленная работа',
        'Возможность влиять на развитие продукта',
        'Обучение и развитие за счет компании',
        'Карьерный рост до Head of Operations',
        'Международный контракт (Web3)',
        'Работа с передовыми AI технологиями'
      ],
      applicationMessage: 'Здравствуйте! Меня заинтересовала вакансия Супервизор'
    },
    {
      id: 'it-developer',
      title: 'IT Разработчик',
      department: 'Backend / ML / AI',
      location: 'Удаленно',
      salaryRange: '$4000-8000/месяц',
      description: 'Мы ищем талантливых разработчиков для создания инновационной AI-платформы знакомств. Ты будешь работать с передовыми технологиями в области машинного обучения, обработки естественного языка и Web3.',
      responsibilities: [
        'Разработка и оптимизация backend на Node.js/Python',
        'Создание и обучение ML моделей для анализа личности',
        'Интеграция AI API (OpenAI, Claude, Gemini)',
        'Разработка алгоритмов матчинга на основе психопрофилей',
        'Работа с большими данными и векторными базами данных',
        'Оптимизация производительности и масштабируемости',
        'Написание технической документации'
      ],
      requirements: [
        'Опыт backend разработки от 3 лет (Node.js/Python)',
        'Знание ML/AI библиотек (TensorFlow, PyTorch, scikit-learn)',
        'Опыт работы с AI API (OpenAI, Anthropic, Google AI)',
        'Понимание NLP и обработки текстов',
        'Опыт работы с PostgreSQL/MongoDB',
        'Знание Git, Docker, CI/CD',
        'Английский язык на уровне B2+ для чтения документации',
        'Опыт с Web3 технологиями будет большим плюсом'
      ],
      benefits: [
        'Конкурентная зарплата: $4000-8000/месяц',
        'Equity опционы в Web3 проекте',
        '100% удаленная работа из любой точки мира',
        'Гибкий график работы',
        'Работа с cutting-edge AI технологиями',
        'Бюджет на обучение и конференции',
        'Возможность роста до Tech Lead/CTO',
        'Международный контракт (Web3)',
        'Современный технологический стек'
      ],
      applicationMessage: 'Здравствуйте! Меня заинтересовала вакансия IT Разработчик (Backend/ML/AI)'
    }
  ];

  const heroContent = {
    title: 'Карьера в AI Dating',
    subtitle: 'Присоединяйтесь к команде будущего',
    description: 'Мы создаем инновационный Web3 проект в сфере AI-знакомств. Работайте удаленно с передовыми технологиями.'
  };

  const handlePrimaryCTA = () => {
    document.getElementById('job-listings')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSecondaryCTA = () => {
    window.open('https://wa.me/66823204796?text=Здравствуйте!%20У%20меня%20есть%20вопросы%20о%20карьере%20в%20AI%20Dating', '_blank');
  };

  return (
    <>
      <Helmet>
        <title>Карьера - AI Dating Service | Вакансии в Web3 и AI</title>
        <meta name="description" content="Присоединяйтесь к команде AI Dating! Открытые вакансии: Менеджер-интервьюер, Лидогенератор, Супервизор, IT разработчик. 100% удаленная работа." />
        <meta name="keywords" content="карьера, вакансии, ai dating, web3, удаленная работа, менеджер, разработчик" />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Navigation */}
        <nav className="bg-white shadow-sm sticky top-0 z-50">
          <div className="max-w-content mx-auto px-4 sm:px-6 py-4">
            <div className="flex items-center justify-between">
              <a href="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
                <Icon name="ArrowLeft" size={20} />
                <span className="font-medium">Вернуться на главную</span>
              </a>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                  <Icon name="Heart" size={20} color="white" />
                </div>
                <span className="text-xl font-bold">AI Dating</span>
              </div>
            </div>
          </div>
        </nav>

        <HeroSection 
          content={heroContent}
          onPrimaryCTA={handlePrimaryCTA}
          onSecondaryCTA={handleSecondaryCTA}
        />

        {/* Job Listings */}
        <section className="py-16" id="job-listings">
          <div className="max-w-content mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Открытые вакансии
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                У нас есть {jobListings.length} открытые позиции. Найдите свою идеальную роль в команде будущего.
              </p>
            </div>

            <div className="grid gap-8 max-w-5xl mx-auto">
              {jobListings.map((job) => (
                <JobCard key={job.id} job={job} />
              ))}
            </div>

            {/* CTA Section */}
            <div className="mt-16 bg-gradient-to-br from-primary to-primary/80 rounded-2xl p-8 md:p-12 text-center text-white">
              <h3 className="text-2xl md:text-3xl font-bold mb-4">
                Не нашли подходящую вакансию?
              </h3>
              <p className="text-lg mb-8 text-white/90 max-w-2xl mx-auto">
                Мы всегда открыты к талантливым людям! Отправьте нам свое резюме, 
                и мы свяжемся с вами, когда появится подходящая позиция.
              </p>
              <a
                href="https://wa.me/66823204796?text=Здравствуйте!%20Я%20хочу%20отправить%20свое%20резюме%20в%20AI%20Dating"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white text-primary px-8 py-4 rounded-full font-semibold hover:bg-white/90 transition-all transform hover:scale-105"
              >
                <Icon name="Send" size={20} />
                Отправить резюме
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default CareersPage;